import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Language from '@mui/icons-material/Language';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import styles from './Login.module.css';
import { loginUser } from '../../services/apiService';

const Alert = React.forwardRef(({ severity, onClose, children }, ref) => (
    <MuiAlert
        elevation={6}
        ref={ref}
        variant="filled"
        severity={severity}
        onClose={onClose}
        sx={{ width: '100%' }}
    >
        {children}
    </MuiAlert>
));

function Login() {
    const { t } = useTranslation();
    const [langOpen, setLangOpen] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (localStorage.getItem('token')) {
            navigate('/');
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await loginUser(username, password);
            navigate('/');
        } catch (err) {
            const serverMsg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                err?.message ||
                t('invalid_credentials');
            let translatedMessage;
            if (typeof serverMsg === 'string') {
                const lowerMsg = serverMsg.toLowerCase();
                if (
                    lowerMsg.includes('request limit') ||
                    lowerMsg.includes('too many requests') ||
                    err?.response?.status === 429
                ) {
                    translatedMessage = t('request_limit_exceeded');
                } else if (lowerMsg.includes('invalid')) {
                    translatedMessage = t('invalid_credentials');
                } else {
                    translatedMessage = serverMsg;
                }
            }
            setError(translatedMessage || t('invalid_credentials'));
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.formContainer}>
                <div className={styles.langContainer}>
                    <button
                        type="button"
                        className={styles.langButton}
                        onClick={() => setLangOpen(!langOpen)}
                    >
                        <Language fontSize="small" />
                        <span className={styles.langText}>{t('change_language')}</span>
                    </button>

                    {langOpen && (
                        <div className={styles.langDropdown}>
                            <button
                                type="button"
                                onClick={() => {
                                    i18n.changeLanguage('fa');
                                    setLangOpen(false);
                                }}
                            >
                                {t('language_fa')}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    i18n.changeLanguage('en');
                                    setLangOpen(false);
                                }}
                            >
                                {t('language_en')}
                            </button>
                        </div>
                    )}
                </div>

                <div className={styles.formHeader}>
                    <h2 className={styles.title}>{t('login_title')}</h2>
                </div>

                {loading ? (
                    <div className={styles.spinner}>{t('login_loading')}</div>
                ) : (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <input
                            placeholder={t('login_email_placeholder')}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className={styles.input}
                            required
                        />
                        <div className={styles.passwordWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t('login_password_placeholder')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={styles.passwordInput}
                                required
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </button>
                        </div>
                        <button type="submit" className={styles.button} disabled={loading}>
                            {t('login_submit')}
                        </button>
                        <button
                            type="button"
                            className={styles.forgotPassword}
                            onClick={() => navigate('/forget-password')}
                        >
                            {t('forgot_password_link')}
                        </button>
                    </form>
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error || t('invalid_credentials')}
                    </Alert>
                </Snackbar>
            </div>
            <div className={styles.leftImage} />
        </div>
    );
}

export default Login;
